import Time from "./time"
import Vue from "vue"
import { loadSaleBanner } from "../../api/utils"
import moment from "moment-timezone"
import {
  convertPointToPrice,
  isPageStatic
} from "./util.utils"
import { MIXINS } from "@/utils/mixins"

const state = {
  saleBanner: null,
  hideGoProBanner: true,
  showGameContainer: false,
  profileDropdownShowing: false,
  navbarDrawerShowing: false,
  pageLoading: false,
  closedSaleBanner: false,
  zenDeskEventAdded: false,
  loadingSaleBanner: true,
  notifications: [],
  coverLoading: false,
}

const getters = {
  hideGoProBanner: state => state.hideGoProBanner,
  showGameContainer: state => state.showGameContainer,
  profileDropdownShowing: state => state.profileDropdownShowing,
  navbarDrawerShowing: state => state.navbarDrawerShowing,
  pageLoading: state => state.pageLoading,
  coverLoading: state => state.coverLoading,
  saleBanner: state => state.saleBanner,
  closedSaleBanner: state => state.closedSaleBanner,
  loadingSaleBanner: state => state.loadingSaleBanner,
  hideSaleBanner: state => {
    if (!state.saleBanner) {
      return true
    }
    const latestBanner = state.saleBanner
    const currentTime = moment.utc()
    const activeUntilTime = moment.utc(latestBanner.active_until.date)
    const timeDiff = activeUntilTime.diff(currentTime)
    return timeDiff < 0 || state.closedSaleBanner
  },
  notifications: state => state.notifications,
  isMobile: () => mq => {
    return ["sx", "xs"].includes(mq)
  },
  minDesktop: () => mq => ["lg", "xl", "xxl"].includes(mq),
  largeDesktop: () => mq => ["xl", "xxl"].includes(mq),
  getPriceByPoint: () => points => convertPointToPrice(points),
  isPageStatic: () => page => isPageStatic(page),
}

const mutations = {
  setHideGoProBanner: (state, status) => (state.hideGoProBanner = status),
  setShowGameContainer: (state, status) => (state.showGameContainer = status),
  setProfileDropdownShowing: (state, status) =>
    (state.profileDropdownShowing = status),
  setNavbarDrawerShowing: (state, status) =>
    (state.navbarDrawerShowing = status),
  setPageLoading: (state, status) => (state.pageLoading = status),
  setCoverLoading: (state, status) => (state.coverLoading = status),
  setSaleBanner: (state, banner) => (state.saleBanner = banner),
  setClosedSaleBanner: (state, status) => (state.closedSaleBanner = status),
  zenDeskEventAdded: (state, status) => (state.zenDeskEventAdded = status),
  setLoadingSaleBanner: (state, status) => (state.loadingSaleBanner = status),
  setNotifications: (state, notifications) =>
    (state.notifications = notifications),
}

const actions = {
  startHelpChat ({ commit, state }) {
    MIXINS.methods.trackAnalyticsEvent("Need Help")
    Vue.prototype.$zendesk.zE("webWidget", "show")
    Vue.prototype.$zendesk.zE("webWidget", "open")
    if (!state.zenDeskEventAdded) {
      Vue.prototype.$zendesk.zE("webWidget:on", "chat:start", function() {
        Vue.prototype.$zendesk.zE("webWidget", "chat:addTags", ["proguides_web_widget"])
      })
      commit("zenDeskEventAdded", true)
    }
  },
  loadGoProBannerStatus ({ commit }) {
    const hideBanner = window.localStorage.getItem(
      "hide-main-navigation-cta-banner"
    )
    commit("setHideGoProBanner", hideBanner)
  },
  toggleGameContainer ({ state, commit }) {
    commit("setShowGameContainer", !state.showGameContainer)
  },
  toggleProfileDropdown ({ commit }) {
    commit("setProfileDropdownShowing", !state.profileDropdownShowing)
  },
  async loadSaleBanner ({ commit, dispatch, rootGetters }) {
    await Promise.all([
      dispatch("User/loadUserCapabilities", null, { root: true }),
      dispatch("loadClosedSaleBanner"),
    ])
    const data = await loadSaleBanner()
    const isUserPro = !!rootGetters["User/isUserPro"]
    const relevantSales = data.filter(saleBanner => {
      // Here, we make the assumption that promo codes of the `product-offer`
      // type are used only for pro membership subscriptions
      const promoCodeType = saleBanner.promo_code?.type
      const promoCodeCategory = saleBanner.promo_code?.metadata?.product_category
      const isSaleForProMembership =
        promoCodeType === "product-offer" ||
        !promoCodeCategory || promoCodeCategory === "proguides_membership"
      return !isSaleForProMembership || !isUserPro
    })
    const displayedSaleBanner = relevantSales[0] ?? null
    if (displayedSaleBanner) {
      commit("setSaleBanner", displayedSaleBanner)
    }
    commit("setLoadingSaleBanner", false)
  },
  loadClosedSaleBanner ({ commit, state }) {
    const latestBanner = state.saleBanner
    if (latestBanner) {
      const activeUntilTime = moment.utc(latestBanner.active_until.date)
      const storageKey = `${
        state.saleBanner.promo_code.code
      }${activeUntilTime.format("YYYY")}`
      const closedBanner = window.localStorage.getItem(storageKey)
      commit("setClosedSaleBanner", closedBanner)
    }
  },
  addNotification ({ commit, state, dispatch }, { type, message }) {
    const notifications = state.notifications
    const notification = {
      type,
      message,
      timestamp: Date.now(),
    }
    notifications.push(notification)
    commit("setNotifications", notifications)
    setInterval(() => {
      dispatch("removeNotification", notification)
    }, 3000)
  },
  removeNotification ({ commit, state }, notification) {
    const notifications = state.notifications.filter(
      notif => notif.timestamp !== notification.timestamp
    )
    commit("setNotifications", notifications)
  },
  appendKlaviyo ({ rootGetters }) {
    const user = rootGetters["User/getUser"]
    if (!user || !user.email) {
      return
    }
    setTimeout(() => {
      try {
        window._learnq.push([
          "identify",
          {
            $email: user.email,
          },
        ])
      } catch (error) {
        console.error(error)
      }
    }, 1000)
  },
  updateCoverLoading ({ commit }, status) {
    commit("setCoverLoading", status)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
  modules: {
    Time,
  },
}



// WEBPACK FOOTER //
// ./src/store/utils/util.module.js